import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import React, { Fragment, useEffect, useState } from 'react';
import {
	FieldConfiguration,
	FrontendExternalDataCallConfiguration,
	GroupFieldConfiguration,
} from '../../../api/ConfigApi';
import { PRIMARY_BUTTON_STYLE } from '../../../data/constants';
import { MediaResponse } from '../../../types/media';
import {
	FieldGroupCreateRequest,
	FieldGroupsByLocatorMap,
	FieldValues,
	FieldValuesMap,
} from '../../../types/policy';
import FieldLabel from '../../fields/FieldLabel';
import FieldEditorForm from '../FieldEditorForm';
import { getNewFieldValues } from '../fieldEditorUtils';

interface Props {
	labelId: string;
	field: GroupFieldConfiguration;
	externalDataCalls: FrontendExternalDataCallConfiguration[];
	fieldGroupLocators?: FieldValues;
	fieldGroupsByLocator: FieldGroupsByLocatorMap;
	tabularDatasourcesHandler: any;
	userHasWriteAccess?: boolean;
	isEditable: boolean;
	updateGroupFieldValue(
		name: string,
		values: FieldValuesMap,
		index: number,
		fieldGroupLocator?: string,
	): void;
	addFieldGroup(fieldName: string): void;
	removeFieldGroup(fieldLocator: string, index: number, name: string): void;
	addFieldGroups?: FieldGroupCreateRequest[]; // endorsement add new field groups array
	currentFieldValues: FieldValuesMap; // current field values are the pending changes that the user is making
	mediaByLocator: { [locator: string]: MediaResponse };
	configVersion: string;
}

type ExpandedPanels = { [key: string]: boolean };

const GroupField = (props: Props) => {
	const {
		field,
		isEditable,
		tabularDatasourcesHandler,
		userHasWriteAccess,
		fieldGroupLocators,
		fieldGroupsByLocator,
		externalDataCalls,
		updateGroupFieldValue,
		removeFieldGroup,
		addFieldGroup,
		addFieldGroups,
		currentFieldValues,
		mediaByLocator,
		labelId,
		configVersion,
	} = props;
	const fieldName = field.name;

	const getFieldGroupLocators = () =>
		fieldGroupLocators ? fieldGroupLocators?.filter((fgl) => !!fgl) : [];
	const addedGroupsForField = addFieldGroups?.filter(
		(f) => f.fieldName === fieldName,
	);
	const [expandedPanels, setExpandedPanels] = useState<ExpandedPanels>({});
	const [expandAll, setExpandAll] = useState(false);

	useEffect(() => {
		const newExpandedPanels: ExpandedPanels = {};

		getFieldGroupLocators().forEach((locator: string, index: number) => {
			newExpandedPanels[locator] = expandedPanels[locator]
				? expandedPanels[locator]
				: false;
		});

		addedGroupsForField?.forEach((afg, index) => {
			newExpandedPanels[`added-${index}`] = expandedPanels[`added-${index}`]
				? expandedPanels[`added-${index}`]
				: false;
		});

		setExpandedPanels(newExpandedPanels);
	}, [addFieldGroups, fieldGroupLocators]);

	const handleChangeExpanded =
		(panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
			setExpandedPanels({ ...expandedPanels, [panel]: newExpanded });
		};

	const handleExpandAll = () => {
		const newExpandedPanels: ExpandedPanels = {};
		Object.keys(expandedPanels).forEach(
			(panelKey) => (newExpandedPanels[panelKey] = !expandAll),
		);
		setExpandedPanels(newExpandedPanels);
		setExpandAll(!expandAll);
	};

	const hasAddedGroup = () => {
		if (!addedGroupsForField) {
			return false;
		}

		return !!addedGroupsForField.length;
	};

	const renderNewFieldGroupButton = () => {
		const handleAdd = async () => {
			await addFieldGroup(field.name);
		};

		if (
			isEditable &&
			((getFieldGroupLocators().length === 0 && !hasAddedGroup()) ||
				field.repeatable)
		) {
			return (
				<Button
					color='primary'
					onClick={handleAdd}
					style={PRIMARY_BUTTON_STYLE}
					disabled={!isEditable}
				>
					<span style={{ color: 'white' }}>Add New</span>
				</Button>
			);
		} else {
			return null;
		}
	};

	const renderRemoveFieldGroupButton = (locator: string, index: number) => {
		if (isEditable) {
			return (
				<div style={{ alignSelf: 'flex-end' }}>
					<Button
						color='primary'
						onClick={() => removeFieldGroup(locator, index, field.name)}
						style={PRIMARY_BUTTON_STYLE}
						disabled={!isEditable}
					>
						<span style={{ color: 'white' }}>Remove</span>
					</Button>
				</div>
			);
		} else {
			return null;
		}
	};

	/**
	 *
	 * @param locator Locator of existing field group or 'added-{index}' for added field groups. null is
	 * passed in place of locator for updateGroupFieldValue, renderRemoveFieldGroupButton, and the
	 * groupIndex prop if it is an added field group.
	 * @param index The relative index inside either existing field group or added field group array
	 */
	const renderGroup = (
		values: FieldValuesMap = {},
		index: number,
		locator: string,
	): JSX.Element => {
		const isAdded = locator.split('-')[0] === 'added';

		const updateFieldValue = (
			subField: FieldConfiguration,
			value: FieldValues,
		) => {
			const newFieldValues = getNewFieldValues(subField, value, values, null);

			if (updateGroupFieldValue) {
				updateGroupFieldValue(
					field.name,
					newFieldValues,
					index,
					isAdded ? null : locator,
				);
			}
		};

		const fieldTitle = isAdded ? `Added: ${field.title}` : field.title;

		return (
			<Accordion
				key={locator}
				expanded={expandedPanels[locator] ? expandedPanels[locator] : false}
				onChange={handleChangeExpanded(locator)}
			>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					aria-controls={`${locator}-content`}
					id={`panel${locator}-header`}
				>
					<Typography>{`${index + 1}. ${fieldTitle}`}</Typography>
				</AccordionSummary>
				<AccordionDetails>
					<FieldEditorForm
						fieldConfigurations={field.fields}
						externalDataCalls={externalDataCalls}
						fieldValues={values}
						currentFieldValues={currentFieldValues}
						isEditable={isEditable}
						tabularDatasourcesHandler={tabularDatasourcesHandler}
						updateFieldValue={updateFieldValue}
						updateGroupFieldValue={null}
						fieldGroupsByLocator={null}
						userHasWriteAccess={userHasWriteAccess}
						groupIndex={isAdded ? `${index}` : locator}
						mediaByLocator={mediaByLocator}
						configVersion={configVersion}
					/>
					{renderRemoveFieldGroupButton(isAdded ? null : locator, index)}
				</AccordionDetails>
			</Accordion>
		);
	};

	const renderGroups = () => {
		return getFieldGroupLocators().map((locator: string, index: number) =>
			renderGroup(fieldGroupsByLocator[locator], index, locator),
		);
	};

	const renderAddFieldGroups = () => {
		if (!addedGroupsForField) {
			return null;
		}

		return addedGroupsForField.map((afg, index) =>
			renderGroup(afg.fieldValues, index, `added-${index}`),
		);
	};

	return (
		<Fragment>
			<Box display='flex' alignItems='center' mb={1}>
				<FieldLabel
					style={{
						height: 'auto',
						color: 'rgba(0, 0, 0, 0.87)',
						transform: 'none',
						transformOrigin: 'unset',
					}}
					id={labelId}
					required={!field.optional}
					title={field.title || ''}
				/>
				{renderNewFieldGroupButton()}
				{Object.keys(expandedPanels || {}).length > 0 ? (
					<Button
						style={{ marginLeft: 'auto' }}
						onClick={handleExpandAll}
						endIcon={
							<ExpandMoreIcon
								style={{
									transform: expandAll ? 'rotate(180deg)' : 'rotate(0deg)',
									transition:
										'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
								}}
							/>
						}
					>
						{expandAll ? 'Collapse All' : 'Expand All'}
					</Button>
				) : null}
			</Box>
			{renderGroups()}
			{renderAddFieldGroups()}
		</Fragment>
	);
};

export default GroupField;
